import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import CardContainer from "@/components/CardContainer";
import { QrCode } from "@/components/qrcode";
import { useOneLinkDeeplink } from "@/hooks/useOneLinkDeeplink";
import { useAuthStore } from "@/stores/useAuth";

// Plain OneLink fallback — kept in sync with DownloadAppBanner.tsx.
const ONELINK_URL = "https://shortsapp.onelink.me/RpbD/0hxetmhb";

export const Route = createFileRoute("/_external/get-app")({
  component: RouteComponent,
  // The QR's promise is an auto-login, which only a real account's tokenized
  // OneLink keeps. An anonymous device user (who also holds an access token)
  // goes to their platform's store listing instead, through `/download`.
  beforeLoad: () => {
    if (!useAuthStore.getState().currentUser?.providers.length) {
      throw redirect({ to: "/download", replace: true });
    }
  },
});

// Desktop lands here from the "Get the App" buttons (the nav bar's and the
// bottom banner's): the tokenized OneLink is minted on this session and shown
// as a QR, so the phone that scans it auto-authenticates without fetching
// anything itself.
function RouteComponent() {
  const { t } = useTranslation();
  const { deeplink, loading } = useOneLinkDeeplink();

  return (
    <div className="mx-auto flex min-h-[60vh] flex-col items-center justify-center px-4">
      <CardContainer className="flex w-full max-w-sm flex-col items-center gap-6 rounded-xl p-8 text-center">
        <img
          src="/SugarBaby.webp"
          alt={t("get_app_logo_alt")}
          className="size-20 rounded-lg"
        />
        <div className="space-y-2">
          <h1 className="text-xl font-bold">{t("get_app_title")}</h1>
          <p className="text-sm text-neutral-400">{t("get_app_qr_message")}</p>
        </div>
        <QrCode
          url={deeplink ?? ONELINK_URL}
          loading={loading}
          size={480}
          className="size-60 overflow-hidden rounded-sm"
          data-test-id="download-app-qr"
        />
      </CardContainer>
    </div>
  );
}
